import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators, UntypedFormArray, UntypedFormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { cloneDeep, findIndex, each, has } from 'lodash';
import moment from 'moment';

import { COMPANIES_PATHS, CompanyOwnershipInterface, StreetType } from '../../companies';
import { fieldsDescription, CompaniesStepFieldsValidationInterface } from './companies-create-edit-widget';
import { ConfirmModalComponent } from '../../../../shared/modals/confirm-modal/confirm-modal.component';
import { CompaniesService } from '../../companies.service';
import { Enums } from '../../../../configs/Enums';
import { DateTimePickerDateObj } from '../../../../ui/date-time-picker/date-time-picker.component';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-companies-create-edit-widget',
  templateUrl: './companies-create-edit-widget.component.html',
  styleUrls: ['./companies-create-edit-widget.component.scss'],
  standalone: false,
})
export class CompaniesCreateEditWidgetComponent implements OnInit {
  public dataLoading: boolean;
  public createEditLoading: boolean;
  public companyId: string;
  public currentSelectedCompany;
  public addresses_equal = true;
  public companyCreateEditForm: UntypedFormGroup;
  public fieldsInvalid;
  public fieldsDescription: CompaniesStepFieldsValidationInterface = fieldsDescription;
  public streetTypes;
  public ownershipTypes;
  public ownershipTypeInit;
  public laStreetTypeInit;
  public paStreetTypeInit;
  public effectiveDateInit;
  public expirationDateInit;
  public isEditMode: boolean;
  public versions;
  public companyVersions;
  public backendError: string;
  public expirationDateEndOpts = { minDate: null, maxDate: null };

  constructor(
    public modalService: NgbModal,
    private fb: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    public companiesService: CompaniesService
  ) {}

  get domains() {
    return this.companyCreateEditForm.get('domains') as UntypedFormArray;
  }

  ngOnInit() {
    this.dataLoading = false;
    this.createEditLoading = false;

    this.route.paramMap.subscribe((params) => {
      this.companyId = params.get('campaign_id');
      this.isEditMode = !!this.companyId;

      if (this.companyId) {
        this.getEditData();
      } else {
        this.getCreateDate();
      }
    });
  }

  private getCreateDate(): void {
    this.dataLoading = true;

    this.companiesService.getCreateCompanyUnion().subscribe(
      (response) => {
        this.ownershipTypes = response[0]['data'];
        this.streetTypes = response[1]['data'];
        this.makeCreateForm();
        this.dataLoading = false;
      },
      (error) => {
        console.log(error);
        this.dataLoading = false;
      }
    );
  }

  private getEditData(): void {
    this.dataLoading = true;

    this.companiesService.getEditCompanyUnion(this.companyId).subscribe(
      (response) => {
        this.ownershipTypes = response[0]['data'];
        this.streetTypes = response[1]['data'];
        this.companyVersions = response[2]['data'];
        this.makeCompanyVersions();
        this.makeEditForm();
        this.dataLoading = false;
      },
      (error) => {
        console.log(error);
        this.dataLoading = false;
      }
    );
  }

  private makeCompanyVersions(): void {
    this.currentSelectedCompany = this.companyVersions[this.companyVersions.length - 1];
    this.versions = this.companyVersions.map((ver) => {
      return ver.version_number;
    });
  }

  private makeCreateForm(): void {
    this.companyCreateEditForm = new UntypedFormGroup({
      ownership_type: new UntypedFormControl('', Validators.required),
      ps_id: new UntypedFormControl(null),
      ps_name: new UntypedFormControl('', Validators.required),
      legal_postcode: new UntypedFormControl('', Validators.required),
      la_country: new UntypedFormControl('', Validators.required),
      la_region_name: new UntypedFormControl('', Validators.required),
      la_city_type: new UntypedFormControl(''),
      la_city_name: new UntypedFormControl('', Validators.required),
      la_street_type: new UntypedFormControl('', Validators.required),
      la_street_name: new UntypedFormControl('', Validators.required),
      la_house_number: new UntypedFormControl('', Validators.required),
      la_house_building: new UntypedFormControl(),
      la_office_number: new UntypedFormControl(),
      physical_postcode: new UntypedFormControl(''),
      pa_country: new UntypedFormControl(''),
      pa_region_name: new UntypedFormControl(''),
      pa_city_type: new UntypedFormControl(''),
      pa_city_name: new UntypedFormControl(''),
      pa_street_type: new UntypedFormControl(''),
      pa_street_name: new UntypedFormControl(''),
      pa_house_number: new UntypedFormControl(''),
      pa_house_building: new UntypedFormControl(),
      pa_office_number: new UntypedFormControl(),
      ps_contract: new UntypedFormControl('', Validators.required),
      effective_date: new UntypedFormControl('', Validators.required),
      expiration_date: new UntypedFormControl('', Validators.required),
      domains: this.fb.array([this.fb.control('', Validators.required)]),
      is_visible: new UntypedFormControl(1),
      addresses_equal: new UntypedFormControl(true),
      is_main: new UntypedFormControl(false),
      upsell_enabled: new UntypedFormControl(false),
      partner_code: new UntypedFormControl(''),
    });

    this.initUiData();
    this.onChangesFormSubscribe();
  }

  private makeEditForm(): void {
    const compData = this.currentSelectedCompany;
    this.companyCreateEditForm = new UntypedFormGroup({
      ownership_type: new UntypedFormControl(compData.ps_id, Validators.required),
      ps_id: new UntypedFormControl(compData.ps_id),
      ps_name: new UntypedFormControl(compData.ps_name, Validators.required),
      legal_postcode: new UntypedFormControl(compData.legal_postcode, Validators.required),
      la_country: new UntypedFormControl(compData.la_country, Validators.required),
      la_region_name: new UntypedFormControl(compData.la_region_name, Validators.required),
      la_city_type: new UntypedFormControl(compData.la_city_type),
      la_city_name: new UntypedFormControl(compData.la_city_name, Validators.required),
      la_street_type: new UntypedFormControl(compData.la_street_type, Validators.required),
      la_street_name: new UntypedFormControl(compData.la_street_name, Validators.required),
      la_house_number: new UntypedFormControl(compData.la_house_number, Validators.required),
      la_house_building: new UntypedFormControl(compData.la_house_building),
      la_office_number: new UntypedFormControl(compData.la_office_number),
      physical_postcode: new UntypedFormControl(compData.addresses_equal ? compData.legal_postcode : compData.physical_postcode),
      pa_country: new UntypedFormControl(compData.addresses_equal ? compData.la_country : compData.pa_country),
      pa_region_name: new UntypedFormControl(compData.addresses_equal ? compData.la_region_name : compData.pa_region_name),
      pa_city_type: new UntypedFormControl(compData.addresses_equal ? compData.la_city_type : compData.pa_city_type),
      pa_city_name: new UntypedFormControl(compData.addresses_equal ? compData.la_city_name : compData.pa_city_name),
      pa_street_type: new UntypedFormControl(compData.addresses_equal ? compData.la_street_type : compData.pa_street_type),
      pa_street_name: new UntypedFormControl(compData.addresses_equal ? compData.la_street_name : compData.pa_street_name),
      pa_house_number: new UntypedFormControl(compData.addresses_equal ? compData.la_house_number : compData.la_house_number),
      pa_house_building: new UntypedFormControl(compData.addresses_equal ? compData.la_house_building : compData.la_house_building),
      pa_office_number: new UntypedFormControl(compData.addresses_equal ? compData.la_office_number : compData.pa_office_number),
      ps_contract: new UntypedFormControl(compData.ps_contract, Validators.required),
      effective_date: new UntypedFormControl(compData.effective_date, Validators.required),
      expiration_date: new UntypedFormControl(compData.expiration_date, Validators.required),
      domains: this.fb.array(
        compData.domains.map((domain) => {
          return this.fb.control(domain.domain, Validators.required);
        })
      ),
      is_visible: new UntypedFormControl(compData.is_visible),
      addresses_equal: new UntypedFormControl(compData.addresses_equal),
      is_main: new UntypedFormControl(compData.is_main),
      upsell_enabled: new UntypedFormControl(compData.upsell_enabled),
      partner_code: new UntypedFormControl(compData.partner_code),
    });

    this.initUiData();
    this.onChangesFormSubscribe();
  }

  public onChangesFormSubscribe(): void {
    this.updateFieldsValidators(this.currentSelectedCompany ? this.currentSelectedCompany.addresses_equal : true);

    if (!this.isEditMode) {
      this.companyCreateEditForm.controls['is_visible'].disable();
      this.companyCreateEditForm.controls['is_visible'].updateValueAndValidity();
    }

    this.companyCreateEditForm.get('addresses_equal').valueChanges.subscribe((isChecked) => {
      this.updateFieldsValidators(isChecked);
    });

    this.companyCreateEditForm.get('upsell_enabled').valueChanges.subscribe((isChecked) => {
      this.updatePartnerCode(isChecked);
    });
  }

  private updateFieldsValidators(isChecked: boolean): void {
    const validatorsField = [
      'physical_postcode',
      'pa_region_name',
      'pa_country',
      'pa_city_name',
      'pa_street_type',
      'pa_street_name',
      'pa_house_number',
      'pa_house_building',
      'pa_office_number',
    ];

    this.addresses_equal = isChecked;

    validatorsField.forEach((field) => {
      if (isChecked) {
        this.companyCreateEditForm.controls[field].disable();
        this.companyCreateEditForm.controls[field].clearValidators();
        this.companyCreateEditForm.controls[field].updateValueAndValidity();
      } else {
        this.companyCreateEditForm.controls[field].enable();

        if (field !== 'pa_house_building' && field !== 'pa_office_number') {
          this.companyCreateEditForm.controls[field].setValidators([Validators.required]);
        }

        if (field === 'physical_postcode') {
          this.companyCreateEditForm.controls[field].setValidators([Validators.pattern(/^.{6,}$/)]);
        }

        this.companyCreateEditForm.controls[field].updateValueAndValidity();
      }
    });
  }

  private updatePartnerCode(isChecked: boolean): void {
    if (isChecked) {
      this.companyCreateEditForm.controls['partner_code'].enable();
      this.companyCreateEditForm.controls['partner_code'].setValidators([Validators.required]);
      this.companyCreateEditForm.controls['partner_code'].updateValueAndValidity();
    } else {
      this.companyCreateEditForm.controls['partner_code'].disable();
      this.companyCreateEditForm.controls['partner_code'].clearValidators();
      this.companyCreateEditForm.controls['partner_code'].updateValueAndValidity();
    }
  }

  private initUiData(): void {
    const ownershipId = (this.currentSelectedCompany && this.currentSelectedCompany.ownership_type.ot_id) || 1;
    const ownershipIndex = findIndex(this.ownershipTypes, {
      ot_id: ownershipId,
    });
    const laStreetTypeId = (this.currentSelectedCompany && this.currentSelectedCompany.la_street_type) || 1;
    const laStreetTypeIndex = findIndex(this.streetTypes, {
      st_id: laStreetTypeId,
    });
    const paStreetTypeId =
      this.currentSelectedCompany && this.currentSelectedCompany.addresses_equal
        ? this.currentSelectedCompany.la_street_type
        : (this.currentSelectedCompany && this.currentSelectedCompany.pa_street_type) || 1;
    const paStreetTypeIndex = findIndex(this.streetTypes, {
      st_id: paStreetTypeId,
    });

    if (ownershipIndex > -1) {
      this.onChangeOwnershipType(this.ownershipTypes[ownershipIndex]);
      this.ownershipTypeInit = this.ownershipTypes[ownershipIndex];
    }

    if (laStreetTypeIndex > -1) {
      this.onChangeLaStreetType(this.streetTypes[laStreetTypeIndex]);
      this.laStreetTypeInit = this.streetTypes[laStreetTypeIndex];
    }

    if (paStreetTypeIndex > -1) {
      this.onChangeFaStreetType(this.streetTypes[paStreetTypeIndex]);
      this.paStreetTypeInit = this.streetTypes[paStreetTypeIndex];
    }

    this.updatePartnerCode(this.currentSelectedCompany && this.currentSelectedCompany.upsell_enabled);

    this.effectiveDateInit = this.currentSelectedCompany ? moment(this.currentSelectedCompany.effective_date) : null;
    this.expirationDateInit = this.currentSelectedCompany ? moment(this.currentSelectedCompany.expiration_date) : null;
  }

  public addDomain(): void {
    this.domains.push(this.fb.control('', Validators.required));
  }

  public removeDomain(index: number): void {
    this.domains.controls.splice(index, 1);
    this.companyCreateEditForm.value.domains.splice(index, 1);
    this.domains.updateValueAndValidity();
    this.companyCreateEditForm.updateValueAndValidity();
  }

  public onChangeOwnershipType(ownershipType: CompanyOwnershipInterface): void {
    this.companyCreateEditForm.controls['ownership_type'].setValue((ownershipType && ownershipType.ot_id) || null);
  }

  public onChangeLaStreetType(streetType: StreetType): void {
    this.companyCreateEditForm.controls['la_street_type'].setValue((streetType && streetType.st_id) || null);
  }

  public onChangeFaStreetType(streetType: StreetType): void {
    this.companyCreateEditForm.controls['pa_street_type'].setValue((streetType && streetType.st_id) || null);
  }

  public onChangeBeginPeriodDate(date: DateTimePickerDateObj): void {
    this.companyCreateEditForm.controls['effective_date'].setValue(moment(date.dateMillis).format('YYYY-MM-DDTHH:mm'));

    this.expirationDateEndOpts = {
      minDate: moment(date.dateMillis).add(1, 'day').format('YYYY-MM-DDTHH:mm'),
      maxDate: null,
    };
  }

  public onChangeEndPeriodDate(date: DateTimePickerDateObj): void {
    this.companyCreateEditForm.controls['expiration_date'].setValue(moment(date.dateMillis).format('YYYY-MM-DDTHH:mm'));
  }

  public onChangeCompanyVisible(event): void {
    /** Получаем текущее состояние чекбокса. **/
    const isChecked = event.target.checked;

    /** Возвращаем состояние на предыдущее, т.е отменяем состояние. **/
    event.target.checked = !event.target.checked;

    /** Проверяем включили или выключили чекбокс. Если включили, возвращаем чекбокс во включенное состояние.
         Если выключаем показываем модалку-подтверждение и в зависимости от нажатой кнопки включаем, либо не включаем
         чекбокс **/
    if (isChecked) {
      event.target.checked = true;
    } else {
      this.openConfirmCompanyChangeVisibleDialog().result.then(
        () => {
          event.target.checked = false;
        },
        () => {
          event.target.checked = true;
        }
      );
    }
  }

  private openConfirmCompanyChangeVisibleDialog(): NgbModalRef {
    const visibleModal = this.modalService.open(ConfirmModalComponent, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'modal-wrapper',
      backdrop: 'static',
    });

    visibleModal.componentInstance.title = 'Подтвердите отключение';
    visibleModal.componentInstance.description =
      'Профиль' +
      ' ' +
      this.currentSelectedCompany.ownership_type.ot_short +
      ' ' +
      '"' +
      this.currentSelectedCompany.ps_name +
      '" ' +
      'будет скрыт. ' +
      'Вы не сможете зарегистрировать нового сотрудника, назначать заявки и работать с участками, ' +
      'закрепленными за этой компанией. Вы действительно хотите скрыть этот профиль?';
    visibleModal.componentInstance.successButtonName = 'Да, отключить';
    visibleModal.componentInstance.abortButtonName = 'Отмена';

    return visibleModal;
  }

  public onSaveStepCallback(): void {
    const fieldsInvalid = [];

    this.fieldsInvalid = null;

    each(this.companyCreateEditForm.controls, (value, key) => {
      if (this.companyCreateEditForm.controls[key].invalid) {
        fieldsInvalid.push(key);
      }
    });

    this.fieldsInvalid = fieldsInvalid;
    this.backendError = null;

    if (!fieldsInvalid.length) {
      if (this.isEditMode) {
        const confirmCloseStepsComponent = this.modalService.open(ConfirmModalComponent, {
          ariaLabelledBy: 'modal-basic-title',
          windowClass: 'modal-wrapper',
          backdrop: 'static',
        });

        confirmCloseStepsComponent.componentInstance.title = 'Подтвердите сохранение';
        confirmCloseStepsComponent.componentInstance.description =
          'Вы внесли изменения в данные комании' +
          ' ' +
          this.currentSelectedCompany.ownership_type.ot_short +
          ' ' +
          '"' +
          this.currentSelectedCompany.ps_name +
          '"' +
          '. Вы действительно хотите сохранить изменения?';
        confirmCloseStepsComponent.componentInstance.successButtonName = 'Да, сохранить';
        confirmCloseStepsComponent.componentInstance.abortButtonName = 'Отмена';

        confirmCloseStepsComponent.result.then(
          () => {
            this.saveOrEditCompany();
          },
          () => {}
        );
      } else {
        this.saveOrEditCompany();
      }
    }
  }

  public onResetStepsCallback(): void {
    const confirmCloseStepsComponent = this.modalService.open(ConfirmModalComponent, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'modal-wrapper',
      backdrop: 'static',
    });

    confirmCloseStepsComponent.componentInstance.title = 'Подтвердите выход без сохранения';
    confirmCloseStepsComponent.componentInstance.description = 'Вся введенная вами информация будет утеряна.' + ' Вы действительно хотите покинуть страницу без сохранения?';
    confirmCloseStepsComponent.componentInstance.successButtonName = 'Да, выйти';
    confirmCloseStepsComponent.componentInstance.abortButtonName = 'Отмена';

    confirmCloseStepsComponent.result.then(
      () => {
        this.router.navigate([COMPANIES_PATHS.companies]);
      },
      () => {}
    );
  }

  public saveOrEditCompany(): void {
    const formFields = cloneDeep(this.companyCreateEditForm.value);
    if (formFields.addresses_equal) {
      formFields['physical_postcode'] = formFields.legal_postcode;
      formFields['pa_country'] = formFields.la_country;
      formFields['pa_region_name'] = formFields.la_region_name;
      formFields['pa_city_type'] = formFields.la_city_type;
      formFields['pa_city_name'] = formFields.la_city_name;
      formFields['pa_street_type'] = formFields.la_street_type;
      formFields['pa_street_name'] = formFields.la_street_name;
      formFields['pa_house_number'] = formFields.la_house_number;
      formFields['pa_house_building'] = formFields.la_house_building || null;
      formFields['pa_office_number'] = formFields.la_office_number || null;
    }

    formFields['domains'] = formFields['domains'].map((domain) => {
      return { domain: domain };
    });

    /** При использовании reactive forms заблоченных полей нет в списке this.companyCreateEditForm.value :( **/
    if (!this.isEditMode) {
      formFields['is_visible'] = 1;
    } else {
      formFields['is_visible'] = +formFields['is_visible'];
    }

    if (!formFields['upsell_enabled']) {
      formFields['partner_code'] = null;
    }

    this.createEditLoading = true;

    this.companiesService.createCompany(formFields).subscribe(
      (response) => {
        this.router.navigate([COMPANIES_PATHS.companies]);
        this.createEditLoading = false;
      },
      (error) => {
        console.log(error);
        if (has(error, 'error.data.non_field_errors')) {
          this.backendError = error.error.data['non_field_errors'][0];
        } else if (has(error, 'error.metadata.message')) {
          this.backendError = error.error.metadata.message;
        } else {
          this.backendError = Enums.errorCodes[Enums.errorCodesMaps['BACKEND_ERROR']];
        }
        this.createEditLoading = false;
      }
    );
  }
}
